import { isEmergencyDepartment } from "./facilityTypes";

/**
 * Diagnostic Capability Registry v1.1 (26 keys).
 * Contract: docs/DIAGNOSTIC_CAPABILITY_REGISTRY_V1.md
 */
export const DIAGNOSTIC_CAPABILITY_OPTIONS = [
  { key: "xray_chest", label: "Chest X-ray", category: "Imaging" },
  { key: "xray_extremity", label: "Extremity X-ray", category: "Imaging" },
  { key: "us_soft_tissue", label: "Soft-tissue ultrasound", category: "Imaging" },
  { key: "us_pelvic", label: "Pelvic / OB-GYN ultrasound", category: "Imaging" },
  { key: "us_vascular_doppler", label: "Vascular Doppler ultrasound", category: "Imaging" },
  { key: "echo", label: "Echocardiogram", category: "Imaging" },
  { key: "ct_head", label: "CT head", category: "Imaging" },
  { key: "ct_abdomen_pelvis", label: "CT abdomen/pelvis", category: "Imaging" },
  { key: "cta", label: "CT angiography", category: "Imaging" },
  { key: "mri", label: "MRI", category: "Imaging" },
  { key: "cbc", label: "CBC (Complete Blood Count)", category: "Lab" },
  { key: "bmp", label: "Basic Metabolic Panel", category: "Lab" },
  { key: "urinalysis", label: "Urinalysis", category: "Lab" },
  { key: "rapid_strep", label: "Rapid Strep", category: "Lab" },
  { key: "rapid_flu", label: "Rapid Flu", category: "Lab" },
  { key: "rapid_covid", label: "Rapid COVID", category: "Lab" },
  { key: "hcg", label: "Pregnancy test (hCG)", category: "Lab" },
  { key: "glucose_poc", label: "Point-of-care glucose", category: "Lab" },
  { key: "troponin", label: "Troponin", category: "Lab" },
  { key: "d_dimer", label: "D-dimer", category: "Lab" },
  { key: "ecg", label: "ECG / EKG", category: "Procedures" },
  { key: "iv_fluids", label: "IV Fluids", category: "Procedures" },
  { key: "laceration_repair", label: "Laceration repair", category: "Procedures" },
  { key: "abscess_i_and_d", label: "Abscess I&D", category: "Procedures" },
  { key: "splinting", label: "Splinting", category: "Procedures" },
  { key: "fracture_management", label: "Fracture reduction / casting", category: "Procedures" },
];

export const DIAGNOSTIC_CAPABILITY_KEYS = DIAGNOSTIC_CAPABILITY_OPTIONS.map(
  (o) => o.key,
);

export const DIAGNOSTIC_IMAGING_CAPABILITY_KEYS = [
  "xray_chest",
  "xray_extremity",
  "us_soft_tissue",
  "us_pelvic",
  "us_vascular_doppler",
  "echo",
  "ct_head",
  "ct_abdomen_pelvis",
  "cta",
  "mri",
];

export const DIAGNOSTIC_LAB_CAPABILITY_KEYS = [
  "cbc",
  "bmp",
  "urinalysis",
  "rapid_strep",
  "rapid_flu",
  "rapid_covid",
  "hcg",
  "glucose_poc",
  "troponin",
  "d_dimer",
];

export const DIAGNOSTIC_PROCEDURE_CAPABILITY_KEYS = [
  "ecg",
  "iv_fluids",
  "laceration_repair",
  "abscess_i_and_d",
  "splinting",
  "fracture_management",
];

/**
 * Needs that block care-level downgrade out of the ER.
 * INVARIANT: strict subset of DIAGNOSTIC_CAPABILITY_KEYS.
 * See: docs/DIAGNOSTIC_CAPABILITY_REGISTRY_V1.md
 */
export const ER_CLASS_CAPABILITY_KEYS = Object.freeze([
  "ct_head",
  "ct_abdomen_pelvis",
  "cta",
  "mri",
  "troponin",
]);

export const DIAGNOSTIC_CAPABILITY_CATEGORY_ORDER = [
  "Imaging",
  "Lab",
  "Procedures",
];

export const DIAGNOSTIC_CAPABILITY_KEYS_BY_CATEGORY = {
  Imaging: DIAGNOSTIC_IMAGING_CAPABILITY_KEYS,
  Lab: DIAGNOSTIC_LAB_CAPABILITY_KEYS,
  Procedures: DIAGNOSTIC_PROCEDURE_CAPABILITY_KEYS,
};

/** UI subgroup labels for Status Board (optional grouping). */
export const DIAGNOSTIC_CAPABILITY_UI_GROUPS = [
  {
    id: "imaging_xray",
    title: "Imaging — X-ray",
    keys: ["xray_chest", "xray_extremity"],
  },
  {
    id: "imaging_us",
    title: "Imaging — Ultrasound",
    keys: ["us_soft_tissue", "us_pelvic", "us_vascular_doppler", "echo"],
  },
  {
    id: "imaging_advanced",
    title: "Imaging — Advanced",
    keys: ["ct_head", "ct_abdomen_pelvis", "cta", "mri"],
  },
  {
    id: "lab_core",
    title: "Lab — Core",
    keys: ["cbc", "bmp", "urinalysis"],
  },
  {
    id: "lab_poc",
    title: "Lab — POC / rapid",
    keys: ["rapid_strep", "rapid_flu", "rapid_covid", "hcg", "glucose_poc"],
  },
  {
    id: "lab_cardiac",
    title: "Lab — Cardiac / clot",
    keys: ["troponin", "d_dimer"],
  },
  {
    id: "procedures",
    title: "Procedures",
    keys: [
      "ecg",
      "iv_fluids",
      "laceration_repair",
      "abscess_i_and_d",
      "splinting",
      "fracture_management",
    ],
  },
];

export const CAPABILITY_VERIFICATION_STATUS = Object.freeze({
  VERIFIED: "verified",
  DUE_SOON: "due_soon",
  OVERDUE: "overdue",
  UNVERIFIED: "unverified",
});

export const CAPABILITY_VERIFICATION_ELIGIBLE_STATUSES = Object.freeze([
  CAPABILITY_VERIFICATION_STATUS.VERIFIED,
  CAPABILITY_VERIFICATION_STATUS.DUE_SOON,
]);

export const EMERGENCY_DEPARTMENT_TYPE = "Emergency Department";

/** @param {object | null | undefined} locationDoc */
export function isEmergencyDepartmentLocation(locationDoc) {
  return isEmergencyDepartment(locationDoc);
}

/**
 * Capabilities used for CareMap matching and facility cards.
 * EDs assume the full v1.1 set until they store a non-empty downgrade list.
 *
 * @param {object | null | undefined} locationDoc
 * @returns {string[]}
 */
export function getEffectiveDiagnosticCapabilities(locationDoc) {
  const stored = locationDoc?.diagnosticCapabilities;
  if (isEmergencyDepartmentLocation(locationDoc)) {
    if (Array.isArray(stored) && stored.length > 0) {
      return stored.filter((key) => DIAGNOSTIC_CAPABILITY_KEYS.includes(key));
    }
    return [...DIAGNOSTIC_CAPABILITY_KEYS];
  }
  if (!Array.isArray(stored)) return [];
  return stored.filter((key) => DIAGNOSTIC_CAPABILITY_KEYS.includes(key));
}
